/**
 * THE one marker colour resolver. A marker's colour is a property OF THE MARKER,
 * never of its position in an array.
 *
 * WHY THIS EXISTS. The curing chart, the /graph marker lines and the temperature
 * chart each carried a byte-identical private copy of this conversion, and the
 * temp-over-time SERIES on /graph did not use it at all — it coloured each trace
 * from a separate 13-entry hex palette indexed by loop position
 * (`getMarkerColorForIndex`). Markers are created with an 18-step HSL gradient
 * stored on the marker as `C`. Two palettes of different lengths, one keyed by
 * identity and one by index, cannot agree: the vertical line for marker N and the
 * trace it produces were different colours, so an operator could not tell which
 * trace belonged to which marker. That is what Rob Stensby reported on the call.
 *
 * INDEX IS THE WRONG KEY, and not only because it disagrees. Indexes shift when a
 * marker is added or removed, so an index-keyed palette RESHUFFLES EVERY COLOUR
 * the moment the list changes — the one thing a colour used for identification
 * must never do. `C` is assigned once at creation and persisted with the marker,
 * so it survives add, remove, reorder and reload.
 *
 * Kept as a pure module rather than a service so it is assertable without a
 * TestBed, matching `length-format.ts` and `project-grid-view.ts`.
 */

/** The colour a marker falls back to when it carries none. */
export const MARKER_FALLBACK_COLOR = '#7F30F3';

/**
 * A marker's colour, as a hex string SciChart accepts.
 *
 * Markers are persisted with `C` in the `"r, g, b"` form produced by the HSL
 * gradient at creation, but older rows — and anything round-tripped through a
 * palette — carry `"#rrggbb"` already. Both must render, so hex passes through
 * untouched and only the triplet form is converted.
 *
 * ANY malformed value yields the fallback rather than an exception: a marker with
 * a bad colour must still be VISIBLE, because an invisible marker reads as a
 * deleted one and this codebase has a history of turning a defect into a blank.
 */
export function markerColor(c: string | null | undefined): string {
  if (!c) {
    return MARKER_FALLBACK_COLOR;
  }

  const raw = c.trim();
  if (raw.startsWith('#')) {
    return raw;
  }

  const parts = raw.split(',').map((s) => s.trim());
  if (parts.length !== 3) {
    return MARKER_FALLBACK_COLOR;
  }

  const channels = parts.map((p) => Number.parseInt(p, 10));
  if (channels.some((n) => !Number.isFinite(n))) {
    return MARKER_FALLBACK_COLOR;
  }

  const toHex = (n: number) =>
    Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');

  return `#${toHex(channels[0])}${toHex(channels[1])}${toHex(channels[2])}`;
}
